import { useNavigate } from 'react-router-dom';
import { useUserType } from '../hooks/useUserType';

/**
 * Custom hook for centralized navigation utilities
 * @returns {Object} Navigation utility functions
 */
export const useNavigationUtils = () => {
  const navigate = useNavigate();
  const { getDashboardRoute } = useUserType();

  /**
   * Navigate back to the appropriate dashboard based on user type
   * Uses the UserTypeService to determine the correct dashboard route
   */
  const navigateBackToDashboard = async () => {
    try {
      const dashboardRoute = await getDashboardRoute();
      navigate(dashboardRoute);
    } catch (error) {
      console.error('Error getting dashboard route:', error);
      // Fallback to generic dashboard
      navigate('/dashboard');
    }
  };

  /**
   * Navigate to a specific route
   * @param {string} route - The route to navigate to
   */
  const navigateTo = (route) => {
    navigate(route);
  };

  /**
   * Navigate back to the previous page
   */
  const navigateBack = () => {
    navigate(-1);
  };

  return {
    navigateBackToDashboard,
    navigateTo,
    navigateBack,
    navigate // Expose the original navigate function for other uses
  };
};

/**
 * Standalone function to get dashboard route (for use outside of React components)
 * @param {Function} getDashboardRouteFunc - The getDashboardRoute function from useUserType
 * @returns {Promise<string>} The dashboard route
 */
export const getDashboardRouteStandalone = async (getDashboardRouteFunc) => {
  try {
    return await getDashboardRouteFunc();
  } catch (error) {
    console.error('Error getting dashboard route:', error);
    return '/dashboard'; // Fallback
  }
};

export default useNavigationUtils;